import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { intelligenceAPI, QualificationTemplate } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ArrowLeft,
  Save,
  SendHorizonal,
  Plus,
  Trash2,
  Loader2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Settings2,
  ListChecks,
  Target,
  Eye,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/errorUtils';
import TemplatePreview from '../components/questionnaire/TemplatePreview';
import { QuestionEditor } from './QuestionEditor';
import {
  LocalQuestion,
  LocalSection,
  TEMPLATE_TYPES,
  SECTION_COLORS,
  makeQuestion,
  makeSection,
  sectionFromApi,
} from './templateBuilderUtils';

export default function TemplateBuilder() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Template settings
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<QualificationTemplate['template_type']>('compliance');
  const [passScore, setPassScore] = useState(70);
  const [instructions, setInstructions] = useState('');
  const [autoCalculateScore, setAutoCalculateScore] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [allowAttachments, setAllowAttachments] = useState(true);
  const [templateStatus, setTemplateStatus] = useState<QualificationTemplate['status']>('draft');

  // Sections
  const [sections, setSections] = useState<LocalSection[]>([makeSection()]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(id ? parseInt(id, 10) : null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load existing template in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadTemplate(parseInt(id, 10));
    }
  }, [id, isEditMode]);

  // Default all sections to open
  useEffect(() => {
    const open: Record<string, boolean> = {};
    sections.forEach((s) => { open[s._key] = openSections[s._key] !== false; });
    setOpenSections(open);
  }, [sections.length]);

  const loadTemplate = async (templateIdToLoad: number) => {
    try {
      setLoading(true);
      const response = await intelligenceAPI.getTemplate(templateIdToLoad);
      const t = response.data;

      setName(t.name);
      setDescription(t.description || '');
      setTemplateType(t.template_type);
      setPassScore(t.pass_score);
      setInstructions(t.instructions || '');
      setAutoCalculateScore(t.auto_calculate_score);
      setRequiresApproval(t.requires_approval);
      setAllowAttachments(t.allow_attachments);
      setTemplateStatus(t.status);
      setTemplateId(t.id);

      if (t.sections && t.sections.length > 0) {
        const sorted = [...t.sections].sort((a, b) => a.sort_order - b.sort_order);
        setSections(sorted.map(sectionFromApi));
      } else {
        setSections([makeSection()]);
      }
    } catch (err: unknown) {
      console.error('Error loading template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to load template'));
      navigate('/questionnaires?tab=templates');
    } finally {
      setLoading(false);
    }
  };

  // -- Section helpers --
  const addSection = useCallback(() => {
    setSections((prev) => [...prev, makeSection()]);
  }, []);

  const removeSection = useCallback((key: string) => {
    setSections((prev) => prev.length > 1 ? prev.filter((s) => s._key !== key) : prev);
  }, []);

  const updateSection = useCallback((key: string, field: keyof LocalSection, value: unknown) => {
    setSections((prev) =>
      prev.map((s) => (s._key === key ? { ...s, [field]: value } : s))
    );
  }, []);

  const moveSectionUp = useCallback((idx: number) => {
    if (idx === 0) return;
    setSections((prev) => {
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr;
    });
  }, []);

  const moveSectionDown = useCallback((idx: number) => {
    setSections((prev) => {
      if (idx >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  }, []);

  // -- Question helpers --
  const addQuestion = useCallback((sectionKey: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s._key === sectionKey
          ? { ...s, questions: [...s.questions, makeQuestion()] }
          : s
      )
    );
  }, []);

  const removeQuestion = useCallback((sectionKey: string, questionKey: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s._key === sectionKey
          ? { ...s, questions: s.questions.filter((q) => q._key !== questionKey) }
          : s
      )
    );
  }, []);

  const updateQuestion = useCallback(
    (sectionKey: string, questionKey: string, field: keyof LocalQuestion, value: unknown) => {
      setSections((prev) =>
        prev.map((s) =>
          s._key === sectionKey
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q._key === questionKey ? { ...q, [field]: value } : q
                ),
              }
            : s
        )
      );
    },
    []
  );

  const moveQuestionUp = useCallback((sectionKey: string, qIdx: number) => {
    if (qIdx === 0) return;
    setSections((prev) =>
      prev.map((s) => {
        if (s._key !== sectionKey) return s;
        const qs = [...s.questions];
        [qs[qIdx - 1], qs[qIdx]] = [qs[qIdx], qs[qIdx - 1]];
        return { ...s, questions: qs };
      })
    );
  }, []);

  const moveQuestionDown = useCallback((sectionKey: string, qIdx: number) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s._key !== sectionKey) return s;
        if (qIdx >= s.questions.length - 1) return s;
        const qs = [...s.questions];
        [qs[qIdx], qs[qIdx + 1]] = [qs[qIdx + 1], qs[qIdx]];
        return { ...s, questions: qs };
      })
    );
  }, []);

  // -- Save --
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      setSaving(true);
      let currentId = templateId;

      const templateData: Partial<QualificationTemplate> = {
        name: name.trim(),
        description: description.trim(),
        template_type: templateType,
        pass_score: passScore,
        instructions: instructions.trim(),
        auto_calculate_score: autoCalculateScore,
        requires_approval: requiresApproval,
        allow_attachments: allowAttachments,
      };

      if (!currentId) {
        const res = await intelligenceAPI.createTemplate(templateData);
        currentId = res.data.id;
        setTemplateId(currentId);
      } else {
        await intelligenceAPI.updateTemplate(currentId, templateData);
      }

      const sectionsPayload = sections.map((s) => ({
        id: s.id,
        title: s.title.trim() || 'Untitled Section',
        description: s.description.trim(),
        weight: s.weight,
        is_required: s.is_required,
        questions: s.questions.map((q) => ({
          id: q.id,
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          help_text: q.help_text.trim(),
          is_required: q.is_required,
          is_scored: q.is_scored,
          max_score: q.max_score,
          scoring_criteria: q.scoring_criteria,
          choices: q.choices,
        })),
      }));

      // CR-4797: rehydrate local state from the server's response so the
      // builder reflects exactly what was persisted, not the in-memory draft.
      // Previously the UI kept the pre-save state even if the backend
      // coerced or dropped any field — users then reopened the record and
      // saw different data, which reads as "Save Draft didn't work".
      const saveResp = await intelligenceAPI.saveTemplateStructure(currentId, sectionsPayload);
      const saved = saveResp.data?.template;
      if (saved?.sections && saved.sections.length > 0) {
        const sorted = [...saved.sections].sort((a, b) => a.sort_order - b.sort_order);
        setSections(sorted.map(sectionFromApi));
      }
      toast.success('Template saved successfully');

      if (!isEditMode) {
        navigate(`/intelligence/templates/${currentId}`, { replace: true });
      }
    } catch (err: unknown) {
      console.error('Error saving template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!templateId) {
      toast.error('Save the template first before publishing');
      return;
    }

    try {
      setPublishing(true);
      await handleSave();
      await intelligenceAPI.publishTemplate(templateId);
      setTemplateStatus('active');
      toast.success('Template published successfully');
    } catch (err: unknown) {
      console.error('Error publishing template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to publish template'));
    } finally {
      setPublishing(false);
    }
  };

  // -- Choice management --
  const addChoice = useCallback((sectionKey: string, questionKey: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s._key === sectionKey
          ? {
              ...s,
              questions: s.questions.map((q) =>
                q._key === questionKey ? { ...q, choices: [...q.choices, ''] } : q
              ),
            }
          : s
      )
    );
  }, []);

  const updateChoice = useCallback(
    (sectionKey: string, questionKey: string, choiceIdx: number, value: string) => {
      setSections((prev) =>
        prev.map((s) =>
          s._key === sectionKey
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q._key === questionKey
                    ? {
                        ...q,
                        choices: q.choices.map((c, i) => (i === choiceIdx ? value : c)),
                      }
                    : q
                ),
              }
            : s
        )
      );
    },
    []
  );

  const removeChoice = useCallback(
    (sectionKey: string, questionKey: string, choiceIdx: number) => {
      setSections((prev) =>
        prev.map((s) =>
          s._key === sectionKey
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q._key === questionKey
                    ? { ...q, choices: q.choices.filter((_, i) => i !== choiceIdx) }
                    : q
                ),
              }
            : s
        )
      );
    },
    []
  );

  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading template...</p>
        </div>
      </div>
    );
  }

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const totalMaxScore = sections.reduce(
    (sum, s) => sum + s.questions.reduce((qSum, q) => qSum + (q.is_scored ? Number(q.max_score) || 0 : 0), 0),
    0
  );

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50/50">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/questionnaires?tab=templates')} className="rounded-full">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-bold text-gray-900">
                      {isEditMode ? 'Edit Template' : 'New Template'}
                    </h1>
                    {templateStatus === 'active' && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                    )}
                    {templateStatus === 'draft' && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">Draft</Badge>
                    )}
                    {templateStatus === 'archived' && (
                      <Badge variant="outline">Archived</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-0.5">
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Layers className="h-3.5 w-3.5" />
                      {sections.length} section{sections.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <ListChecks className="h-3.5 w-3.5" />
                      {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}
                    </span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Target className="h-3.5 w-3.5" />
                      {totalMaxScore} pts max
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button variant="outline" onClick={handleSave} disabled={saving || publishing}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                {templateStatus === 'draft' && (
                  <Button onClick={handlePublish} disabled={saving || publishing} className="bg-emerald-600 hover:bg-emerald-700">
                    {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SendHorizonal className="h-4 w-4 mr-2" />}
                    {publishing ? 'Publishing...' : 'Publish'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
          {/* Template Settings */}
          <Card className="shadow-sm border-gray-200">
            <div className="px-6 py-4 border-b bg-gray-50/80 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-gray-500" />
                <h2 className="text-base font-semibold text-gray-900">Template Settings</h2>
              </div>
            </div>
            <CardContent className="p-6 space-y-6">
              {/* Row 1: Name + Type */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="template-name" className="text-sm font-medium">
                    Template Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="template-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., ISO 9001 Compliance Assessment"
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-type" className="text-sm font-medium">Type</Label>
                  <Select value={templateType} onValueChange={(v) => setTemplateType(v as QualificationTemplate['template_type'])}>
                    <SelectTrigger id="template-type" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div className="space-y-2">
                <Label htmlFor="template-desc" className="text-sm font-medium">Description</Label>
                <Textarea
                  id="template-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this template's purpose..."
                  rows={2}
                />
              </div>

              {/* Row 3: Instructions */}
              <div className="space-y-2">
                <Label htmlFor="template-instructions" className="text-sm font-medium">
                  Instructions for Suppliers
                </Label>
                <Textarea
                  id="template-instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Instructions that will be shown to suppliers when filling out this template..."
                  rows={3}
                />
              </div>

              {/* Row 4: Pass Score + Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="pass-score" className="text-sm font-medium">Pass Score (%)</Label>
                  <Input
                    id="pass-score"
                    type="number"
                    min={0}
                    max={100}
                    value={passScore}
                    onChange={(e) => setPassScore(parseInt(e.target.value, 10) || 0)}
                    className="h-10 w-32"
                  />
                </div>
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Options</Label>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                      <div className="space-y-0.5">
                        <Label htmlFor="auto-score" className="text-sm font-medium cursor-pointer">Auto-calculate score</Label>
                        <p className="text-xs text-muted-foreground">Automatically calculate scores from question weights</p>
                      </div>
                      <Switch id="auto-score" checked={autoCalculateScore} onCheckedChange={setAutoCalculateScore} />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                      <div className="space-y-0.5">
                        <Label htmlFor="requires-approval" className="text-sm font-medium cursor-pointer">Requires approval</Label>
                        <p className="text-xs text-muted-foreground">Submissions need manager approval</p>
                      </div>
                      <Switch id="requires-approval" checked={requiresApproval} onCheckedChange={setRequiresApproval} />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                      <div className="space-y-0.5">
                        <Label htmlFor="allow-attachments" className="text-sm font-medium cursor-pointer">Allow attachments</Label>
                        <p className="text-xs text-muted-foreground">Suppliers can upload supporting documents</p>
                      </div>
                      <Switch id="allow-attachments" checked={allowAttachments} onCheckedChange={setAllowAttachments} />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sections & Questions Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-primary" />
                Sections & Questions
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Build your questionnaire by adding sections and questions
              </p>
            </div>
            <Button onClick={addSection} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Section
            </Button>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, sIdx) => {
              const color = SECTION_COLORS[sIdx % SECTION_COLORS.length];
              const isOpen = openSections[section._key] !== false;

              return (
                <div key={section._key} className={`rounded-xl border-2 ${color.border} overflow-hidden shadow-sm`}>
                  {/* Section Header */}
                  <Collapsible open={isOpen} onOpenChange={() => toggleSection(section._key)}>
                    <CollapsibleTrigger asChild>
                      <div className={`${color.bg} px-5 py-4 cursor-pointer hover:brightness-95 transition-all`}>
                        <div className="flex items-center gap-4">
                          <div className={`flex items-center justify-center h-9 w-9 rounded-lg ${color.badge} font-bold text-sm`}>
                            {sIdx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-semibold text-base ${color.text}`}>
                                {section.title || `Section ${sIdx + 1}`}
                              </h3>
                              {section.is_required && (
                                <Badge variant="outline" className={`text-xs ${color.text} border-current`}>
                                  Required
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-sm text-muted-foreground">
                                {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
                              </span>
                              {section.weight > 0 && (
                                <span className="text-sm text-muted-foreground">
                                  Weight: {section.weight}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); moveSectionUp(sIdx); }}
                                  disabled={sIdx === 0}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Move up</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); moveSectionDown(sIdx); }}
                                  disabled={sIdx === sections.length - 1}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Move down</TooltipContent>
                            </Tooltip>
                            {sections.length > 1 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={(e) => { e.stopPropagation(); removeSection(section._key); }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete section</TooltipContent>
                              </Tooltip>
                            )}
                            <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="px-5 py-5 space-y-5 bg-white">
                        {/* Section Settings */}
                        <div className={`rounded-lg border p-4 ${color.bg} bg-opacity-30`}>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Section Title</Label>
                              <Input
                                value={section.title}
                                onChange={(e) => updateSection(section._key, 'title', e.target.value)}
                                placeholder="e.g., Quality Management"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Weight</Label>
                              <Input
                                type="number"
                                min={0}
                                value={section.weight}
                                onChange={(e) => updateSection(section._key, 'weight', parseInt(e.target.value, 10) || 0)}
                              />
                            </div>
                            <div className="flex items-end">
                              <div className="flex items-center gap-2 h-10 px-3 rounded-md border bg-white">
                                <Switch
                                  checked={section.is_required}
                                  onCheckedChange={(v) => updateSection(section._key, 'is_required', v)}
                                />
                                <Label className="text-sm cursor-pointer">Required</Label>
                              </div>
                            </div>
                          </div>
                          {/* Description row below */}
                          <div className="mt-4 space-y-2">
                            <Label className="text-sm font-medium">Description</Label>
                            <Textarea
                              value={section.description}
                              onChange={(e) => updateSection(section._key, 'description', e.target.value)}
                              placeholder="Optional description for this section..."
                              rows={2}
                            />
                          </div>
                        </div>

                        {/* Questions */}
                        <div className="space-y-3">
                          {section.questions.map((question, qIdx) => (
                            <QuestionEditor
                              key={question._key}
                              question={question}
                              qIdx={qIdx}
                              totalQuestions={section.questions.length}
                              sectionKey={section._key}
                              sectionColor={color}
                              onUpdate={updateQuestion}
                              onRemove={removeQuestion}
                              onMoveUp={moveQuestionUp}
                              onMoveDown={moveQuestionDown}
                              onAddChoice={addChoice}
                              onUpdateChoice={updateChoice}
                              onRemoveChoice={removeChoice}
                            />
                          ))}
                        </div>

                        {/* Add Question Button */}
                        <button
                          onClick={() => addQuestion(section._key)}
                          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Question
                        </button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              );
            })}
          </div>

          {/* Add Section Button */}
          <button
            onClick={addSection}
            className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl text-base font-medium text-gray-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Another Section
          </button>
        </div>
      </div>

      {/* Preview Dialog */}
      <TemplatePreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        templateName={name}
        description={description}
        instructions={instructions}
        passScore={passScore}
        sections={sections}
      />
    </TooltipProvider>
  );
}
